import {Checkbox} from '@wandb/weave/components';
import {type RUN_STATE, RunState} from '@wandb/weave/components/RunState';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {reportTriggerConfigParseError} from '../configParseError';
import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {type Trigger} from '../TriggersTable/utilTypes';
import {
  getRunFilterConfigFromTriggerFilter,
  getRunFilterString,
  isValidRunFilterConfig,
  type RunFilter,
  RunFilterChunk,
  type RunFilterConfig,
} from './RunFilterShared';
import {BoldLabel, SeparatedChunk, SmallText} from './shared';
// NOTE: this order is used for determining how to generate strings for the UI
// so be careful when changing it
const STATE_TO_REPORT = ['RUNNING', 'FINISHED', 'FAILED'] as const;

export type StateToReport = (typeof STATE_TO_REPORT)[number];

export type RunStateFilter = {
  states: StateToReport[];
};

export type RunStateConfig = {
  runStateFilter: RunStateFilter;
  runFilter: RunFilterConfig;
};

const initialRunStateConfig: RunStateConfig = {
  runStateFilter: {states: []},
  runFilter: {
    isRunNameFilterEnabled: false,
    runName: undefined,
    isUsernameFilterEnabled: false,
    username: undefined,
  },
};

export const useRunStateStore = create<RunStateConfig>()(
  devtools(
    () => ({
      ...initialRunStateConfig,
    }),
    {name: 'RunStateStore'}
  )
);

const setRunStateFilter = (runStateFilter: Partial<RunStateFilter>) => {
  useRunStateStore.setState(state => ({
    runStateFilter: {...state.runStateFilter, ...runStateFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunStateStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

export const RunStateActions = {
  init: () => {
    useRunStateStore.setState({
      ...initialRunStateConfig,
    });
  },
  initFromTrigger: (trigger: Trigger) => {
    const runStateConfig = getRunStateConfig(trigger);
    if (runStateConfig) {
      useRunStateStore.setState(runStateConfig);
    }
  },
  updateStateToReport: (stateToReport: StateToReport, isSelected: boolean) => {
    const currentStates = useRunStateStore.getState().runStateFilter.states;
    let newStates = [];
    if (isSelected) {
      newStates = currentStates.includes(stateToReport)
        ? currentStates
        : [...currentStates, stateToReport];
    } else {
      newStates = currentStates.filter(state => state !== stateToReport);
    }
    setRunStateFilter({
      ...useRunStateStore.getState().runStateFilter,
      states: newStates,
    });
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
};

type RunStateCheckboxProps = {
  states: (typeof RUN_STATE)[number][];
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const RunStateCheckbox = ({
  states,
  checked,
  onCheckedChange,
}: RunStateCheckboxProps) => {
  return (
    <div className="flex flex-row items-center gap-8">
      <div className="my-4 flex flex-row items-center gap-8">
        <Checkbox
          size="small"
          checked={checked}
          onCheckedChange={onCheckedChange}
        />
      </div>
      <div className="flex flex-row items-center gap-6">
        {states.map((state, index) => (
          <>
            {/* oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html */}
            <RunState key={index} value={state} />
            {states.length > 1 && index < states.length - 1 && <span>or</span>}
          </>
        ))}
      </div>
    </div>
  );
};

export const RunStateSection = () => {
  const runFilter = useRunStateStore(state => state.runFilter);
  const runStateFilter = useRunStateStore(state => state.runStateFilter);
  return (
    <Tailwind>
      <SeparatedChunk>
        <BoldLabel>Included states</BoldLabel>
        <div className="mb-8 mt-4">
          <SmallText>
            This event will trigger whenever a run status changes to one of the
            selected states.
          </SmallText>
        </div>
        <RunStateCheckbox
          states={['running']}
          checked={runStateFilter.states.includes('RUNNING')}
          onCheckedChange={checked => {
            RunStateActions.updateStateToReport('RUNNING', checked !== false);
          }}
        />
        <RunStateCheckbox
          states={['crashed', 'failed']}
          checked={runStateFilter.states.includes('FAILED')}
          onCheckedChange={checked => {
            RunStateActions.updateStateToReport('FAILED', checked !== false);
          }}
        />
        <RunStateCheckbox
          states={['finished']}
          checked={runStateFilter.states.includes('FINISHED')}
          onCheckedChange={checked => {
            RunStateActions.updateStateToReport('FINISHED', checked !== false);
          }}
        />
      </SeparatedChunk>
      <SeparatedChunk>
        <RunFilterChunk runFilter={runFilter} actions={RunStateActions} />
      </SeparatedChunk>
    </Tailwind>
  );
};

export const isValidRunStateConfig = (runStateConfig: RunStateConfig) => {
  return (
    runStateConfig.runStateFilter.states.length > 0 &&
    runStateConfig.runStateFilter.states.every(state =>
      STATE_TO_REPORT.includes(state)
    ) &&
    isValidRunFilterConfig(runStateConfig.runFilter)
  );
};

export const getRunStateString = (
  runStateFilter: RunStateConfig['runStateFilter'],
  runFilter: RunStateConfig['runFilter']
) => {
  const runFilterStr = getRunFilterString(runFilter);
  // we want to join the statuses with commas, but "or " before the last one. If there's only one status, we just use that.
  const rawStatuses = runStateFilter.states;
  const statuses = STATE_TO_REPORT.filter(state =>
    rawStatuses.includes(state)
  ).map(s => s.toLocaleLowerCase());

  const statusList =
    statuses.length === 1
      ? statuses[0]
      : statuses.slice(0, -1).join(', ') +
        ', or ' +
        statuses[statuses.length - 1];
  let stringEnd = '';
  if (runFilterStr.length > 0) {
    stringEnd += `a run ${runFilterStr} changes status to ${statusList}.`;
  } else {
    stringEnd += `a run changes status to ${statusList}.`;
  }
  return stringEnd;
};

export const getRunStateConfig = (
  trigger: Trigger
): RunStateConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunState
  ) {
    return undefined;
  }
  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);
    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      reportTriggerConfigParseError('run_state', trigger);
      return undefined;
    }
    if (!parsedFilter.run_state_filter?.states) {
      reportTriggerConfigParseError('run_state', trigger);
      return undefined;
    }
    return {
      runStateFilter: {
        states: parsedFilter.run_state_filter.states,
      },
      runFilter,
    };
  } catch (e) {
    // Without this, an unreadable filter throws while the view drawer renders.
    reportTriggerConfigParseError('run_state', trigger, e);
    return undefined;
  }
};
